import type { ReportEvent } from '@/models/reportEvent';
import type { Report } from '@/models/report';

/**
 * Backfill a synthetic SIGNED event for every FINAL report that has no
 * SIGNED event in the audit log.
 *
 * The report-history timeline is built entirely from ReportEvent audit
 * rows. Some reports reach status FINAL without a SIGNED event ever being
 * written — e.g. demo/seed data inserted directly as FINAL, bypassing the
 * finalize flow that normally records one. Those reports would otherwise
 * show no "finalized" entry, or no history at all.
 *
 * Deriving the entry from the report's own state keeps the timeline
 * accurate even when the audit log has gaps. The returned list stays
 * sorted oldest-first to match the API contract mapReportEvents expects.
 */
export function synthesizeSignedEvents(
  events: ReportEvent[],
  reports: Report[]
): ReportEvent[] {
  const reportsWithSignedEvent = new Set(
    events.filter((e) => e.eventType === 'SIGNED').map((e) => e.reportUuid)
  );

  const synthetic: ReportEvent[] = reports
    .filter((r) => r.status === 'FINAL' && !reportsWithSignedEvent.has(r.uuid))
    .map((r) => ({
      uuid: `synthetic-signed-${r.uuid}`,
      eventType: 'SIGNED',
      statusTags: ['FINAL'],
      reportUuid: r.uuid,
      actorFirstName: null,
      actorLastName: null,
      actorUuid: null,
      // Best available "finalized at" timestamp — these reports have no
      // Signature row to read a signing time from.
      createdAt: r.updatedAt,
    }));

  if (!synthetic.length) return events;

  return [...events, ...synthetic].sort(
    (a, b) => Date.parse(a.createdAt) - Date.parse(b.createdAt)
  );
}
